import { useState } from 'react';
import { QuoteCard, AutomatedContentCheck } from './app';
import { type ReviewDecision } from './admin-api';
import {
  quoteModerationCopy as c,
  labels,
  control,
  type QuoteModerationItem,
  type PairDecision,
} from './quote-moderation-model';
export function UnifiedModerationCard({
  item,
  busy,
  onDecision,
  onPairDecision,
  onSkip,
}: {
  item: QuoteModerationItem;
  busy: boolean;
  onPairDecision?(decision: PairDecision, spoilers: boolean): void;
  onSkip?(): void;
  onDecision(decision: ReviewDecision, message?: string | null, spoilers?: boolean): void;
}) {
  const [spoilers, setSpoilers] = useState(item.containsSpoilers);
  return (
    <div>
      <QuoteCard
        quote={item}
        category={item.quote ? c.pair : labels[item.kind]}
        showCaptureDetails={item.kind === 'shared'}
        contentLabel={item.kind === 'thought' ? c.thoughtLabel : c.quoteLabel}
        readerSummary={
          item.kind === 'wording'
            ? `${item.readerCount} ${c.saves} · ${item.status === 'pending' ? c.awaiting : c[item.status]}`
            : undefined
        }
        busyDecision={busy ? { decision: 'approved', reviewId: item.id } : null}
        error={null}
        moderatedDecision={null}
        onDecision={(_, decision, message) => onDecision(decision, message)}
        details={
          item.kind === 'thought' ? (
            <div className="mb-5">
              {item.quote && (
                <section aria-label={c.quoteLabel} className="mb-6">
                  <div className="mb-3 flex flex-wrap justify-between gap-2 text-xs font-bold uppercase text-brass-pale">
                    <span>
                      {c.quoteLabel} · {item.quote.language.toUpperCase()}
                    </span>
                    <span>
                      {item.quote.status === 'approved'
                        ? c.quoteAlreadyApproved
                        : item.quote.status === 'hidden'
                          ? c.hidden
                          : c.awaiting}
                    </span>
                  </div>
                  <blockquote className="review-paper m-0 whitespace-pre-wrap px-6 py-6 font-serif text-lg leading-relaxed text-paper-text">
                    <p className="relative z-[3] m-0">{item.quote.body}</p>
                  </blockquote>
                  <AutomatedContentCheck check={item.contentCheck} contentType="quote" />
                </section>
              )}
              {item.publishedBody && (
                <>
                  <h3 className="text-xs font-bold uppercase text-brass-pale">{c.published}</h3>
                  <p className="text-xs text-ink-muted">{c.declineEditHint}</p>
                  <p className="whitespace-pre-wrap text-sm text-ink-soft">{item.publishedBody}</p>
                </>
              )}
              <h3 className="text-xs font-bold uppercase text-brass-pale">
                {item.publishedBody ? c.edit : c.thoughtLabel}
              </h3>
            </div>
          ) : undefined
        }
        actions={
          item.kind === 'shared' ? undefined : (
            <div>
              {item.kind === 'thought' && (
                <label className="mb-4 flex min-h-11 items-center gap-3 text-sm">
                  <input
                    type="checkbox"
                    disabled={busy}
                    checked={spoilers}
                    onChange={(e) => setSpoilers(e.target.checked)}
                  />
                  {c.spoilers}
                </label>
              )}
              {item.kind === 'thought' && item.quote ? (
                <div className="grid gap-3">
                  <button
                    className={'admin-button-primary'}
                    disabled={busy}
                    onClick={() => onPairDecision?.('approve_both', spoilers)}
                  >
                    {item.quote.status === 'approved' ? c.approveThought : c.approveBoth}
                  </button>
                  <button
                    className={control}
                    disabled={busy}
                    onClick={() => onPairDecision?.('quote_only', spoilers)}
                  >
                    {item.quote.status === 'approved'
                      ? c.keepQuoteDeclineThought
                      : c.approveQuoteDeclineThought}
                  </button>
                  {item.quote.status !== 'approved' && (
                    <button
                      className={'admin-button-danger'}
                      disabled={busy}
                      onClick={() => onPairDecision?.('decline_thought', spoilers)}
                    >
                      {c.declineThoughtOnly}
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {item.status !== 'hidden' && (
                    <button
                      disabled={busy}
                      className={'admin-button-danger'}
                      onClick={() => onDecision('rejected', null, spoilers)}
                    >
                      {item.kind === 'wording' ? c.hide : c.decline}
                    </button>
                  )}
                  {item.status !== 'approved' && (
                    <button
                      disabled={busy}
                      className={'admin-button-primary'}
                      onClick={() => onDecision('approved', null, spoilers)}
                    >
                      {c.approve}
                    </button>
                  )}
                </div>
              )}
            </div>
          )
        }
      />
      {onSkip && (
        <button disabled={busy} onClick={onSkip} className={`${control} mb-6 w-full`}>
          {c.skip}
        </button>
      )}
    </div>
  );
}
